
  import { Vue, Component, Prop, Watch } from "vue-property-decorator";
  import { cloneDeep, isEqual } from "lodash-es";

  import LIST_TEAMS from "@/apollo/teams/ListTeams.gql";
  import HoveredIcon from "@/components/icons/HoveredIcon.vue";
  import GroupModal from "@/components/teams/GroupModalNew.vue";
  import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
  import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";
  import CardPagination from "@/components/card/CardPagination.vue";
  import DefaultContentLayout from "@/components/scaffold/DefaultContentLayout.vue";
  import GroupCard from "@/components/teams/GroupCard.vue";
  import GroupCollectionFilters from "@/components/teams/GroupCollectionFilters.vue";
  import UiButton from "@/components/button/ui-button.vue";
  import PlusIcon from "@/components/icons/PlusIcon.vue";
  import ActionPanel from '@/components/common/ActionsPanel.vue';

  import { getFiltersDataFromQuery, joinQueryValues } from "@/helpers/router/prepareFiltersQuery";

  import paginationConfig from "@/data/paginationConfig";
  import groupsFiltersTemplate from "@/data/filtersTemplates/groupsFiltersTemplate";

  import {
    User,
    Company,
    Team,
    Query_RootTeamArgs,
    Team_Bool_Exp,
    Manager_Contractor,
  } from "@/generated/graphql";
  import Roles from "@/entities/Roles";
  import PaginationConfig from "@/entities/PaginationConfig";
  import GroupsFiltersQuery from "@/entities/filters/GroupsFiltersQuery";
  import PostingStatus from "@/entities/PostingStatus";
  import { getPaginationDataFromQuery, getPaginationQuery } from "@/helpers/router/preparePaginationQuery";
  import { getEmptyFiltersResultMessage } from "@/helpers/lists/emptyListMessages";
  import EmptyListImageGroups from "@/components/icons/emptyListImages/EmptyListImageGroups.vue";

@Component({
  components: {
    EmptyListImageGroups,
    LoadingOverlay,
    EmptyListPlaceholder,
    CardPagination,
    HoveredIcon,
    DefaultContentLayout,
    GroupCard,
    GroupModal,
    GroupCollectionFilters,
    UiButton,
    PlusIcon,
    ActionPanel
  },
  apollo: {
    groups: {
      query: LIST_TEAMS,
      variables() {
        return this.groupFilter;
      },
      skip() {
        return this.skipQuery;
      },
      update(data) {
        this.paginationConfig.total = data.total.aggregate.count;
        this.paginationConfig.loadedCount = data.team.length;
        return data.team;
      },
    },
  },
})
export default class Groups extends Vue {
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({type:Object, required: true}) readonly company!: Company;

  groups: Team[] | null = null;
  selectedGroup: Team | null = null;

  paginationConfig: PaginationConfig = { ...paginationConfig };

  dialog: boolean = false;

  groupFilters: GroupsFiltersQuery = cloneDeep(groupsFiltersTemplate);

  get hasData(): boolean {
    return !this.isLoading && Boolean(this.groups);
  }

  get filtersEmpty(): boolean {
    return isEqual(this.groupFilters, groupsFiltersTemplate);
  }

  get emptyListPlaceholder(): string {
    const entitiesName = "groups";

    if (this.filtersEmpty) {
      return `Start creating groups`;
    }

    return getEmptyFiltersResultMessage(entitiesName);
  }
  
  // get emptyListSubtitle(): string{
  //   return "Start creating groups";
  // }

  get isLoading(): boolean {
    return this.$apollo.queries.groups.loading;
  }

  get currentManagerId(): undefined | string {
    return this.currentUser.role == Roles.MANAGER
    ? this.currentUser.manager?.id
    : undefined
  }

  get managerAccessContractorsIds(): string[] {
    if (this.currentManagerId && this.currentUser.manager?.contractors) {
      return this.currentUser.manager.contractors
        .map((c: Manager_Contractor) => c.contractor_id);
    }
    return [];
  }

  get isChosenPageEmpty(): boolean {
    const collectionEmpty = this.groups?.length === 0;
    const notFirstPage = this.paginationConfig.page !== 1;

    return !this.isLoading && collectionEmpty && notFirstPage;
  }

  get groupFilter(): Query_RootTeamArgs {
    const { page, limit} = this.paginationConfig;
    const nextOffset = page * limit - limit;
    const {
      title,
      locations,
      managers,
      contractors,
      projects,
      postingStatus,
      skills,
      postings,
    } = this.groupFilters;

    const filterConditions: Team_Bool_Exp[] = [];

    if (postingStatus === PostingStatus.ACTIVE) {
      filterConditions.push({
        archived: { _eq: false },
      });
    } else if(postingStatus === PostingStatus.ARCHIVED) {
      filterConditions.push({
        archived: { _eq: true },
      });
    }

    if (title) {
      filterConditions.push({
        name: { _ilike: `%${title}%`},
      });
    }

    if (locations.length) {
      filterConditions.push({
        location: { _in: locations },
      });
    }

    if (contractors.length) {
      filterConditions.push({
        contractors: {
          contractor: {
            id: { _in: contractors }
          }
        }
      });
    }

    if (managers.length) {
      filterConditions.push({
        managers: {
          manager: {
            id: { _in: managers },
          }
        }
      });
    }

    if (projects.length) {
      filterConditions.push({
        projects: {
          project: {
            id: { _in: projects },
          }
        }
      });
    }

    if (postings.length) {
      filterConditions.push({
        postings: {
          posting: {
            job_title: { _in: postings },
          }
        }
      });
    }

    if (skills.length) {
      filterConditions.push({
        skills: {
          skill_id: { _in: skills },
        }
      });
    }

    return {
      offset: nextOffset,
      limit: limit,
      where: {
        _and: filterConditions,
      },
    };
  }

  get queryPropsToWatch(): string {
    return JSON.stringify([
      this.groupFilters,
      this.paginationConfig,
    ]);
  }

  get queryParams(): {filters: GroupsFiltersQuery, pagination: PaginationConfig} {
    const query = this.$route.query;
    const {
      posting_status: postingStatus,
      ...queryParams
    } = query;

    const queryData = {
      postingStatus,
      ...queryParams,
    };

    const filters = getFiltersDataFromQuery<GroupsFiltersQuery>(groupsFiltersTemplate, queryData);
    const pagination = {...this.paginationConfig, ...getPaginationDataFromQuery(query)};

    return {
      filters,
      pagination,
    };
  }

  @Watch("queryPropsToWatch")
  updateQuery(): void {
    if (this.isChosenPageEmpty) {
      return this.showFirstPage();
    }

    const paginationQuery = getPaginationQuery(this.paginationConfig);

    const {
      postingStatus,
      ...filtersData
    } = this.groupFilters;

    const query = joinQueryValues({
      ...paginationQuery,
      ...filtersData,
      posting_status: postingStatus,
    });

    if (isEqual(query, this.$route.query)) {
      return;
    }

    this.$router.push({query}).catch(()=>{});
  }

  @Watch("$route.query", { immediate: true })
  onQueryChange(): void {
    const { filters, pagination } = this.queryParams;

    this.groupFilters = filters;
    this.paginationConfig = pagination;
  }

  created() {
    if (!!this.company) {
      this.$apollo.queries.groups.skip = false;
    }
  }

  showFirstPage(): void {
    this.paginationConfig = {...this.paginationConfig, page: paginationConfig.page };
  }

  updateFilters(filters: GroupsFiltersQuery): void {
    const dataEqual = isEqual(filters, this.groupFilters);

    if (dataEqual) {
      return;
    }

    this.groupFilters = filters;
  }

  resetFilters(): void {
    if (isEqual(this.groupFilters, groupsFiltersTemplate)) {
      return;
    }

    this.groupFilters = cloneDeep(groupsFiltersTemplate);
    this.paginationConfig = { ...paginationConfig };
  }

  toggle(group: Team): void {
    this.selectedGroup = group || null;
    this.dialog = true;
  }

  get empty(): boolean {
    if (!this.groups) {
      return false;
    }
    return this.groups.length === 0 && !this.$apollo.loading;
  }

  get isAdmin():boolean {
    return this.currentUser.role == Roles.ADMIN;
  }

  handleRefetch(): void {
    this.$apollo.queries.groups.refetch();
  }

  prepareFilters(filterValues: Record<string, any>): void {
    this.groupFilters = {
      ...this.groupFilters,
      ...filterValues,
    };
  }
}
